'use client';

import { NonAuthenticatedError } from '@eos/shared-ui/non-authenticated-error/NonAuthenticatedError';
import { SignedOutError } from '@eos/shared-ui/signed-out-error/SignedOutError';
import { Typography } from '@zeus/core/next';
import { FormattedMessage } from 'react-intl';

type ErrorType =
  | 'non-authenticated'
  | 'logged-out'
  | 'access_denied'
  | 'missing_auth_params'
  | 'auth_failed'
  | string;

export interface AuthErrorProps {
  error?: ErrorType;
  signInUrl?: string;
  homePageLinkUrl?: string;
}

const getErrorMessage = (error?: string) => {
  switch (error) {
    case 'missing_auth_params':
      return (
        <FormattedMessage
          id="event-management-portal.auth.error.missing-auth-params"
          defaultMessage="Missing authentication parameters. Please try again."
        />
      );
    case 'auth_failed':
      return (
        <FormattedMessage
          id="event-management-portal.auth.error.auth-failed"
          defaultMessage="Authentication failed. Please try again."
        />
      );
    case 'access_denied':
      return (
        <FormattedMessage
          id="event-management-portal.auth.error.access-denied"
          defaultMessage="Access denied. You don't have permission to access this resource."
        />
      );
    default:
      return (
        <FormattedMessage
          id="event-management-portal.auth.error.unknown"
          defaultMessage="An unexpected error occurred. Please try again."
        />
      );
  }
};

export const AuthError: React.FC<AuthErrorProps> = ({
  error,
  signInUrl = '',
  homePageLinkUrl = '',
}) => {
  const image = error === 'access_denied' ? 'brokensuitcase' : 'default';

  // Use shared components for specific error types
  if (error === 'logged-out' || error === 'access_denied') {
    return (
      <div className="h-[80vh] flex justify-center items-center">
        <SignedOutError signInUrl={signInUrl} image={image} />
      </div>
    );
  }

  // Show specific error message for authentication errors
  if (error === 'missing_auth_params' || error === 'auth_failed') {
    return (
      <div className="h-[80vh] flex justify-center items-center">
        <div className="flex flex-col gap-4 items-center max-w-125">
          <Typography variant="h5" color="error">
            <FormattedMessage
              id="event-management-portal.auth.error.title"
              defaultMessage="Authentication Error"
            />
          </Typography>
          <div className="text-center">
            <Typography variant="body1">{getErrorMessage(error)}</Typography>
          </div>
        </div>
      </div>
    );
  }

  // Fallback to generic error
  return (
    <div className="h-[80vh] flex justify-center items-center">
      <NonAuthenticatedError homePageLinkUrl={homePageLinkUrl} />
    </div>
  );
};
